import { H4, Label, useFormatDate } from "@dgs/core";
import { parseISO } from "date-fns";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { RegistrationShopSummaryResource } from "../../../shared/types";
import { PaymentTransactionItemRow } from "./PaymentTransactionItemRow";
import {
	GreyBox,
	RegistrationShopSummarySection,
	Separator,
	Subheading,
	SummaryAccordionItem,
} from "./ShopSummaryComponents";
import { TotalRefunds } from "./TotalRefunds";

interface Props {
	cancellationSummaryResource: RegistrationShopSummaryResource;
}

export const CancellationShopSummary = ({ cancellationSummaryResource }: Props) => {
	const { t } = useTranslation();
	const formatDate = useFormatDate();

	const toBePaidTaxes = (cancellationSummaryResource?.totalTaxes ?? []).map((t, index) => ({ ...t, index: index + 1 }));

	return (
		<RegistrationShopSummarySection>
			<H4>{t("Cancellation Summary")}</H4>
			{cancellationSummaryResource.transactionItemsToBeRefunded.length > 0 && (
				<>
					<Subheading>{t("To be refunded")}</Subheading>
					<GreyBox>
						{cancellationSummaryResource.transactionItemsToBeRefunded.map((item, index) => (
							<Fragment key={item.id ?? index}>
								<PaymentTransactionItemRow
									item={item}
									taxes={toBePaidTaxes}
									labels={
										item.paidAt && (
											<Label size="small">
												{t("Paid on {{date}}", {
													date: formatDate(parseISO(item.paidAt)),
												})}
											</Label>
										)
									}
								/>
								{index + 1 < cancellationSummaryResource.transactionItemsToBeRefunded.length && <Separator />}
							</Fragment>
						))}
					</GreyBox>
				</>
			)}
			{cancellationSummaryResource.transactionItemsAlreadyProcessed.length > 0 && (
				<SummaryAccordionItem
					title={t("Already refunded")}
					body={
						<GreyBox>
							{cancellationSummaryResource.transactionItemsAlreadyProcessed.map((item, index) => (
								<Fragment key={item.id ?? index}>
									<PaymentTransactionItemRow
										item={item}
										taxes={toBePaidTaxes}
										labels={
											item.paidAt && (
												<Label size="small" color={!item.refundTotalPrice ? "success" : "danger"}>
													{t(!item.refundTotalPrice ? "Paid on {{date}}" : "Refunded on {{date}}", {
														date: formatDate(parseISO(item.paidAt)),
													})}
												</Label>
											)
										}
									/>
									{index + 1 < cancellationSummaryResource.transactionItemsAlreadyProcessed.length && <Separator />}
								</Fragment>
							))}
						</GreyBox>
					}
				></SummaryAccordionItem>
			)}
			<TotalRefunds
				prices={cancellationSummaryResource}
				totalTaxes={cancellationSummaryResource.totalTaxes}
				currencyType={cancellationSummaryResource.transactionItemsToBeRefunded[0].currencyType}
			/>
		</RegistrationShopSummarySection>
	);
};
